import { createSelector } from '@reduxjs/toolkit';
import getMenu from './getMenu';
import getItems from './getItems';
import getNestedItemStockBalancesData from './getNestedItemStockBalancesData';
import getAllCategoryInfos from './getAllCategoryInfos';
import {
  getChoiceSetDiscountConfig,
  getUpsellDisplayLimit,
  getUpsellUnavailableDisplayType,
} from './config';
import {
  checkTimeAvailability,
  validateUpsellItems,
  itemCategoryAvailibilityValidate,
} from '../utils/ordering';
import { UPSELL_ITEM_DISPLAY_TYPE } from '../constants';
import getOrderDateTimeSelected from './getOrderDateTimeSelected';
import processUpsellNameOverride from '../utils/processors/processUpsellNameOverride';
import getUserUnderage from './getUserUnderage';
import { filterAlcoholicItemsFromChoiceSet } from '../utils/ordering/filterAlcoholic';

type Availability = {
  availability?: boolean;
  start?: string | null;
  end?: string | null;
  days?: Day[];
};
export default (item: Item | undefined) =>
  createSelector(
    [
      getMenu,
      getItems,
      getNestedItemStockBalancesData(undefined),
      getUpsellDisplayLimit,
      getUpsellUnavailableDisplayType,
      getAllCategoryInfos,
      getChoiceSetDiscountConfig,
      getOrderDateTimeSelected,
      getUserUnderage,
    ],
    (
      menu,
      items,
      [stockBalanceDataMap],
      upsellAmountByItem = 3,
      unavailableItemDisplay,
      categories,
      choiceSetDiscountConfig,
      dateTimeSelected,
      isUserUnderage,
    ): (Item & { upsellAvailable: boolean })[] => {
      if (!item || !menu) return [];
      const { upsellChoiceSets } = item;
      if (!upsellChoiceSets || !((upsellChoiceSets || []).length > 0) || !items)
        return [];
      const {
        choiceSets,
        rootMenuNode: { subMenus },
      } = menu;

      const set = choiceSets[upsellChoiceSets[0]] as ChoiceSet & Availability;

      if (!set) return [];
      //check upsell choiceset availability
      const setAvailability = checkTimeAvailability({
        availability: Boolean(set.availability),
        days: set.days,
        start: set.start,
        end: set.end,
        dateTimeSelected: dateTimeSelected,
      });

      if (!setAvailability) return [];

      let filteredChoiceSet = { ...set };
      if (isUserUnderage) {
        filteredChoiceSet = filterAlcoholicItemsFromChoiceSet(set);
      }

      //upsell item can not be item itself
      const upSellItems = filteredChoiceSet.choices
        .filter(choice => choice.id !== item.id)
        .map(choice => processUpsellNameOverride({ choice, items }))
        .filter(
          c =>
            c &&
            itemCategoryAvailibilityValidate({
              id: c.id,
              subMenus,
              categoriesInfo: categories || [],
              dateTimeSelected,
            }),
        );

      const validatedUpSellItems = validateUpsellItems(
        upSellItems,
        stockBalanceDataMap,
        choiceSetDiscountConfig,
        dateTimeSelected,
      );

      const result =
        unavailableItemDisplay === UPSELL_ITEM_DISPLAY_TYPE.GREYOUT
          ? validatedUpSellItems
          : validatedUpSellItems?.filter(item => item.upsellAvailable);

      return (result || []).slice(0, upsellAmountByItem);
    },
  );
